/**
 * Menu UI state: per-date "Active Workspace" with dirty tracking.
 */
import { create } from 'zustand';
import type { DayConfig } from '../subscription/types';
import type { MenuBuilder, MenuItemData, BookedItem } from './types';
import { resolveScaledCarbsOnFirstSelect } from '../menu/scaledCarbs';

function normalizeCustomizationsForBooking(customizations: any[]): any[] {
  if (!Array.isArray(customizations)) {return [];}
  return customizations.map((c: any) => {
    const raw_options = (c.options as any[]) ?? [];
    const options = raw_options.map((o: any) => {
      const option_id = Number(o?.option_id ?? o?.id ?? 0);
      return {
        ...o,
        option_id: Number.isFinite(option_id) ? option_id : 0,
        id: Number.isFinite(option_id) ? option_id : 0,
        is_selected: Boolean(o?.is_selected),
      };
    });
    const customization_id = Number(c?.customization_id ?? c?.id ?? 0);
    return {
      ...c,
      customization_id: Number.isFinite(customization_id) ? customization_id : 0,
      id: Number.isFinite(customization_id) ? customization_id : 0,
      is_selected: c.is_selected !== false,
      options,
    };
  });
}

type BookedItemBase = Omit<BookedItem, 'quantity' | 'selection_number'>;

/** One entry per selection (quantity 1 each). Meals get selection_number 1, 2, 3 in order. */
export function buildBookingPayloadFromBuilders(menuBuilders: MenuBuilder[]): BookedItem[] {
  const meal_entries: { sn: number; item: BookedItemBase }[] = [];
  const snack_entries: BookedItem[] = [];

  function pushEntry(base: BookedItemBase, is_meal: boolean, selection_number: number) {
    const entry: BookedItem = { ...base, quantity: 1, selection_number: is_meal ? selection_number : 0 };
    if (is_meal) {
      meal_entries.push({ sn: selection_number, item: base });
    } else {
      snack_entries.push(entry);
    }
  }

  for (const builder of menuBuilders) {
    for (const it of builder.type_data ?? []) {
      const qty = Math.max(0, Number(it.quantity ?? 0) || 0);
      const is_meal = ((it as any).food_type_category ?? builder.type_name ?? 'MEAL').toLowerCase() === 'meal';
      const base_sn = is_meal ? Number((it as any).selection_number ?? 0) : 0;
      const customizations = normalizeCustomizationsForBooking((it as any).customizations ?? []);
      const base: BookedItemBase = {
        food_type_category: (it.food_type_category ?? builder.type_name ?? 'MEAL').toLowerCase() as any,
        food_type_id: Number(it.food_type_id ?? builder.food_type_id) || 0,
        menu_item_id: Number(it.id) || 0,
        customizations,
        menu_builder_id: Number(it.menu_builder_id ?? builder.food_type_id) || 0,
        scaled_carbs: Number((it as any).scaled_carbs ?? 0),
        is_updated_by_customer: true,
        is_replaceable_item: false,
        is_selected: true,
        parent_id: 0,
      };
      for (let i = 0; i < qty; i++) {
        pushEntry(base, is_meal, base_sn || i + 1);
      }
      for (const r of (it as any).replaceable_menu_items ?? []) {
        const rq = Math.max(0, Number(r.quantity ?? 0) || 0);
        const r_meal = ((r as any).food_type_category ?? (it as any).food_type_category ?? builder.type_name ?? 'MEAL').toLowerCase() === 'meal';
        const r_sn = r_meal ? Number((r as any).selection_number ?? 0) : 0;
        const r_customizations = normalizeCustomizationsForBooking((r as any).customizations ?? []);
        const r_base: BookedItemBase = {
          food_type_category: (it.food_type_category ?? builder.type_name ?? 'MEAL').toLowerCase() as any,
          food_type_id: Number((r as { food_type_id?: number }).food_type_id ?? it.food_type_id ?? builder.food_type_id) || 0,
          menu_item_id: Number((r as { id?: number }).id) || 0,
          customizations: r_customizations,
          menu_builder_id: Number((r as any).menu_builder_id ?? builder.food_type_id) || 0,
          scaled_carbs: Number((r as any).scaled_carbs ?? 0),
          is_updated_by_customer: true,
          is_replaceable_item: true,
          is_selected: true,
          parent_id: Number(it.id) || 0,
        };
        for (let i = 0; i < rq; i++) {
          pushEntry(r_base, r_meal, r_sn || i + 1);
        }
      }
    }
  }

  meal_entries.sort((a, b) => a.sn - b.sn);
  const out: BookedItem[] = [];
  meal_entries.forEach((e, idx) => {
    out.push({ ...e.item, quantity: 1, selection_number: idx + 1 });
  });
  out.push(...snack_entries);
  return out;
}

export type MenuDateSlot = {
  menuBuilders: MenuBuilder[];
  dayConfig: DayConfig | null;
  isDoubleBox: boolean;
  /** When true, this slot's data is for box 2 (save with is_double_box: true). When false/undefined, for box 1 (is_double_box: false). */
  isBox2Data?: boolean;
};

/** Bookings the sync would send, normalized to a stable string for "diff vs load / last save" (order-independent). */
export function stableMenuBookingSyncKey(menuBuilders: MenuBuilder[] | undefined): string {
  if (!menuBuilders?.length) {
    return '';
  }
  const items = buildBookingPayloadFromBuilders(menuBuilders);
  if (items.length === 0) {
    return '';
  }
  const parts = items.map(
    b =>
      `${b.menu_item_id}\u{1f}` +
      `${b.food_type_id}\u{1f}` +
      `${b.menu_builder_id}\u{1f}` +
      `${b.selection_number}\u{1f}` +
      `${b.scaled_carbs ?? 0}\u{1f}` +
      `${(b as { parent_id?: number }).parent_id ?? 0}\u{1f}` +
      `${b.is_replaceable_item ? 1 : 0}\u{1f}` +
      `${JSON.stringify(b.customizations ?? [])}`,
  );
  parts.sort();
  return parts.join('\u{1e}');
}

export function isMenuDateSlotDivergentFromServer(
  current: MenuDateSlot | undefined,
  server: MenuDateSlot | undefined,
): boolean {
  if (!current) {
    return false;
  }
  if (!server) {
    return stableMenuBookingSyncKey(current.menuBuilders) !== '';
  }
  if (Boolean(current.isBox2Data) !== Boolean(server.isBox2Data)) {
    return true;
  }
  return (
    stableMenuBookingSyncKey(current.menuBuilders) !==
    stableMenuBookingSyncKey(server.menuBuilders)
  );
}

function withDirtyReconcile(
  state: {
    serverSnapshotByDate: Record<string, MenuDateSlot>;
    syncVersionByDate: Record<string, number>;
    dirtyDates: Set<string>;
  },
  date: string,
  nextSlot: MenuDateSlot,
): { syncVersion: number; dirtyDelta: (s: Set<string>) => void } {
  const snap = state.serverSnapshotByDate[date];
  const divergent = isMenuDateSlotDivergentFromServer(nextSlot, snap);
  if (!divergent) {
    return {
      syncVersion: 0,
      dirtyDelta: next => {
        next.delete(date);
      },
    };
  }
  return {
    syncVersion: (state.syncVersionByDate[date] ?? 0) + 1,
    dirtyDelta: next => {
      next.add(date);
    },
  };
}

/**
 * Merge rule for the menu screen: prefer hydrated slot when `isBox2Data` matches the active box and slot has builders; else use GET (`menuData`).
 * Used by: `MenuFlowScreen` (React Query + store sync effect).
 */
export function pickBuildersForMenuMerge(
  slot: MenuDateSlot | undefined,
  query_builders: MenuBuilder[] | undefined,
  is_box2: boolean,
): {
  builders: MenuBuilder[];
  builders_from_slot: MenuBuilder[] | null;
  slot_matches_box: boolean;
} {
  const slot_matches_box = Boolean(slot?.isBox2Data) === is_box2;
  const builders_from_slot =
    slot_matches_box && slot && (slot.menuBuilders?.length ?? 0) > 0 ? slot.menuBuilders : null;
  const builders_from_query = query_builders ?? [];
  const builders = builders_from_slot ?? builders_from_query;
  return { builders, builders_from_slot, slot_matches_box };
}

/**
 * Box-1 menu builders for dashboard today strip, `cachedMenusByDate`, and similar (not the week chart — that uses subscription particulars).
 * - **divergent from server** (`isMenuDateSlotDivergentFromServer` / in-flight booking ≠ snapshot): same merge as MenuFlow for box 1 — store wins.
 * - **in sync with snapshot**: prefer **GET** when non-empty; if GET empty, use **slot** (prefetch/bootstrap).
 * When the slot holds only box-2 data, the divergent path uses GET for box-1; otherwise uses GET when present.
 * Used by: `useDataFetcher`, `DashboardScreen` (today strip / nutrition), `cachedMenusByDate`.
 */
export function mergeMenuBuildersBox1View(
  slot: MenuDateSlot | undefined,
  query_builders: MenuBuilder[] | undefined,
  is_date_dirty: boolean,
): MenuBuilder[] {
  if (is_date_dirty) {
    return pickBuildersForMenuMerge(slot, query_builders, false).builders;
  }
  const q = query_builders ?? [];
  if (q.length > 0) {
    return q;
  }
  return slot?.menuBuilders ?? [];
}

interface MenuState {
  menuStateByDate: Record<string, MenuDateSlot>;
  serverSnapshotByDate: Record<string, MenuDateSlot>;
  syncVersionByDate: Record<string, number>;
  selectedDate: string;
  dirtyDates: Set<string>;

  setMenuData: (builders: MenuBuilder[], date: string, config: DayConfig | null, isBox2?: boolean) => void;
  setSelectedDate: (date: string) => void;
  setItemCustomizations: (menu_builder_id: number, itemId: number, customizations: any[]) => void;
  updateItemQuantity: (menu_builder_id: number, itemId: number, isPlus: boolean) => { success: boolean; reason?: string };
  updateItemScaledCarbs: (menu_builder_id: number, itemId: number, scaledCarbs: number) => void;
  prepareBookingData: (date?: string) => BookedItem[];
  getSlot: (date: string) => MenuDateSlot | undefined;
  getDirtyDates: () => string[];
  /** True when the booking payload for `date` differs from `serverSnapshotByDate` (initial load / last save baseline). */
  isDateDivergentFromServer: (date: string) => boolean;
  clearDateDirty: (date: string) => void;
  getSyncVersion: (date: string) => number;
  confirmSyncSuccess: (date: string, versionAtSend: number) => void;
  rollbackDate: (date: string) => void;
  /** Wipe all in-memory slots (logout / session switch). */
  resetSession: () => void;
}

function cloneSlot(slot: MenuDateSlot): MenuDateSlot {
  return JSON.parse(JSON.stringify(slot)) as MenuDateSlot;
}

type BuilderAndItem = {
  builder: MenuBuilder;
  item: MenuItemData;
  itemIndex: number;
  replaceableIndex?: number;
};

/** Index of the builder row that contains `found.builder` (same object as in `slot.menuBuilders` when possible). */
function indexOfBuilderForMutation(slot: MenuDateSlot, found: BuilderAndItem): number {
  let idx = slot.menuBuilders.indexOf(found.builder);
  if (idx >= 0) {
    return idx;
  }
  idx = slot.menuBuilders.findIndex(bb => Number(bb.food_type_id) === Number(found.builder.food_type_id));
  return idx;
}

/** Find item by menu_builder_id + item id. Tries exact builder first; fallback by itemId across builders, preferring the builder that matches requested menu_builder_id so the same item in lunch vs dinner updates the correct section. */
function getBuilderAndItem(builders: MenuBuilder[], menu_builder_id: number, itemId: number): BuilderAndItem | null {
  const menu_builder_id_num = Number(menu_builder_id);
  const item_id_num = Number(itemId);

  function findInBuilder(b: MenuBuilder): BuilderAndItem | null {
    const type_data = b.type_data ?? [];
    const item_index = type_data.findIndex(i => Number(i.id) === item_id_num);
    if (item_index >= 0) {return { builder: b, item: type_data[item_index]!, itemIndex: item_index };}
    for (let ti = 0; ti < type_data.length; ti++) {
      const replaceable = (type_data[ti] as any).replaceable_menu_items ?? [];
      const ri = replaceable.findIndex((r: any) => Number(r.id) === item_id_num);
      if (ri >= 0) {return { builder: b, item: replaceable[ri], itemIndex: ti, replaceableIndex: ri };}
    }
    return null;
  }

  const byBuilder = builders.find(b => Number(b.food_type_id) === menu_builder_id_num || Number((b as any).menu_builder_id) === menu_builder_id_num || Number((b as any).id) === menu_builder_id_num);
  if (byBuilder) {
    const found = findInBuilder(byBuilder);
    if (found) {return found;}
  }

  const byItemIdAll: BuilderAndItem[] = [];
  for (const b of builders) {
    const found = findInBuilder(b);
    if (found) {byItemIdAll.push(found);}
  }
  if (byItemIdAll.length === 0) {return null;}
  const preferred = byItemIdAll.find(f => Number(f.builder.food_type_id) === menu_builder_id_num);
  return preferred ?? byItemIdAll[0];
}

function getMealEntriesForCompact(type_data: any[]): { sn: number; setSn: (n: number) => void }[] {
  const entries: { sn: number; setSn: (n: number) => void }[] = [];
  for (const it of type_data) {
    const meal = ((it as any).food_type_category ?? '').toLowerCase() === 'meal';
    const q = it.quantity ?? 0;
    if (meal && q > 0) {
      const sn = Number((it as any).selection_number ?? 0) || 9999;
      entries.push({ sn, setSn: (n: number) => ((it as any).selection_number = n) });
    }
    for (const r of (it as any).replaceable_menu_items ?? []) {
      const rmeal = ((r as any).food_type_category ?? '').toLowerCase() === 'meal';
      const rq = r.quantity ?? 0;
      if (rmeal && rq > 0) {
        const rsn = Number((r as any).selection_number ?? 0) || 9999;
        entries.push({ sn: rsn, setSn: (n: number) => ((r as any).selection_number = n) });
      }
    }
  }
  return entries;
}

function compactMealSelectionInPlace(type_data: any[]): void {
  const entries = getMealEntriesForCompact(type_data);
  entries.sort((a, b) => a.sn - b.sn);
  entries.forEach((e, idx) => e.setSn(idx + 1));
}

/**
 * Total selected item quantities (type_data + replaceable rows).
 * Used by: `setMenuData` stale-GET guard; rejects replacing a non-empty slot with an all-zero snapshot.
 */
export function totalSelectedQuantityFromBuilders(builders: MenuBuilder[]): number {
  let n = 0;
  for (const b of builders) {
    for (const it of b.type_data ?? []) {
      n += Math.max(0, Number(it.quantity ?? 0) || 0);
      for (const r of (it as { replaceable_menu_items?: { quantity?: number }[] }).replaceable_menu_items ?? []) {
        n += Math.max(0, Number(r.quantity ?? 0) || 0);
      }
    }
  }
  return n;
}

function normalizeBuildersForSlot(builders: MenuBuilder[]): MenuBuilder[] {
  return builders.map(b => {
    const builder_id = Number(b.food_type_id);
    const seen_ids = new Set<number>();
    const type_data_raw = (b.type_data ?? []).map(it => {
      const item = { ...it, menu_builder_id: Number(it.menu_builder_id ?? builder_id) };
      const repl = (it as any).replaceable_menu_items;
      if (repl && Array.isArray(repl)) {
        (item as any).replaceable_menu_items = repl.map((r: any) => ({
          ...r,
          menu_builder_id: Number((r as any).menu_builder_id ?? builder_id),
        }));
      }
      return item;
    });
    const type_data = type_data_raw.filter(it => {
      const id = Number(it.id);
      if (seen_ids.has(id)) {return false;}
      seen_ids.add(id);
      return true;
    });
    let selected_quantity = 0;
    for (const it of type_data) {
      selected_quantity += it.quantity ?? 0;
      for (const r of (it as any).replaceable_menu_items ?? []) {
        selected_quantity += r.quantity ?? 0;
      }
    }
    return { ...b, type_data, selected_quantity };
  });
}

export const useMenuStore = create<MenuState>((set, get) => ({
  menuStateByDate: {},
  serverSnapshotByDate: {},
  syncVersionByDate: {},
  selectedDate: '',
  dirtyDates: new Set(),

  setMenuData: (builders, date, config, isBox2) =>
    set(state => {
      const existing = state.menuStateByDate[date];
      const normalized = normalizeBuildersForSlot(builders);
      const is_double = config?.isDoubleBox ?? false;
      const is_box2 = is_double ? (isBox2 ?? false) : false;
      const incoming_qty = totalSelectedQuantityFromBuilders(normalized);
      const existing_qty = existing ? totalSelectedQuantityFromBuilders(existing.menuBuilders) : 0;
      const box_matches = !existing || Boolean(existing.isBox2Data) === is_box2;
      /**
       * A late menu GET (in-flight refetch after sync, or prefetch) can still return qty 0 after `confirmSyncSuccess`
       * cleared `dirty`. The merge effect then called `setMenuData` with that snapshot and wiped the slot.
       */
      const reject_stale_empty_get =
        box_matches && !!existing && existing_qty > 0 && incoming_qty === 0;
      const menu_builders_final = reject_stale_empty_get ? existing.menuBuilders : normalized;
      const was_dirty = state.dirtyDates.has(date);
      const slot: MenuDateSlot = {
        menuBuilders: menu_builders_final,
        dayConfig: config ?? existing?.dayConfig ?? null,
        isDoubleBox: is_double,
        isBox2Data: is_box2,
      };
      const snapshot = cloneSlot(slot);
      const next_menu_state = { ...state.menuStateByDate, [date]: slot };
      const next_snapshot = { ...state.serverSnapshotByDate, [date]: snapshot };
      if (was_dirty) {
        const { syncVersion, dirtyDelta } = withDirtyReconcile(state, date, slot);
        const nextDirty = new Set(state.dirtyDates);
        dirtyDelta(nextDirty);
        return {
          menuStateByDate: next_menu_state,
          syncVersionByDate: { ...state.syncVersionByDate, [date]: syncVersion },
          dirtyDates: nextDirty,
        };
      }
      const nextDirty = new Set(state.dirtyDates);
      nextDirty.delete(date);
      return {
        menuStateByDate: next_menu_state,
        serverSnapshotByDate: next_snapshot,
        syncVersionByDate: { ...state.syncVersionByDate, [date]: 0 },
        dirtyDates: nextDirty,
      };
    }),

  setSelectedDate: date => set({ selectedDate: date }),

  setItemCustomizations: (menu_builder_id, itemId, customizations) => {
    const { menuStateByDate, selectedDate } = get();
    const slot = menuStateByDate[selectedDate];
    if (!slot?.menuBuilders?.length) {return;}
    const found = getBuilderAndItem(slot.menuBuilders, menu_builder_id, itemId);
    if (!found) {return;}
    const customization_builder_index = indexOfBuilderForMutation(slot, found);
    if (customization_builder_index < 0) {return;}
    const nextSlot = cloneSlot(slot);
    const b = nextSlot.menuBuilders[customization_builder_index];
    if (b?.type_data) {
      if (found.replaceableIndex != null) {
        const it = b.type_data[found.itemIndex];
        const repl = (it as any).replaceable_menu_items;
        if (repl?.[found.replaceableIndex]) {
          (repl[found.replaceableIndex] as any).customizations = customizations;
        }
      } else {
        const it = b.type_data[found.itemIndex];
        if (it) {
          (it as any).customizations = customizations;
        }
      }
    }
    set(state => {
      const d = state.selectedDate;
      const { syncVersion, dirtyDelta } = withDirtyReconcile(state, d, nextSlot);
      const nextDirty = new Set(state.dirtyDates);
      dirtyDelta(nextDirty);
      return {
        menuStateByDate: { ...state.menuStateByDate, [d]: nextSlot },
        syncVersionByDate: { ...state.syncVersionByDate, [d]: syncVersion },
        dirtyDates: nextDirty,
      };
    });
  },

  getSlot: date => get().menuStateByDate[date],

  isDateDivergentFromServer: date => {
    const s = get();
    return isMenuDateSlotDivergentFromServer(s.menuStateByDate[date], s.serverSnapshotByDate[date]);
  },

  getDirtyDates: () => Array.from(get().dirtyDates),

  clearDateDirty: date =>
    set(s => {
      const next = new Set(s.dirtyDates);
      next.delete(date);
      return { dirtyDates: next };
    }),

  getSyncVersion: date => get().syncVersionByDate[date] ?? 0,

  confirmSyncSuccess: (date, versionAtSend) => {
    const state = get();
    if ((state.syncVersionByDate[date] ?? 0) !== versionAtSend) {return;}
    const slot = state.menuStateByDate[date];
    if (!slot) {return;}
    set(s => ({
      serverSnapshotByDate: { ...s.serverSnapshotByDate, [date]: cloneSlot(slot) },
      dirtyDates: (() => {
        const next = new Set(s.dirtyDates);
        next.delete(date);
        return next;
      })(),
    }));
  },

  rollbackDate: date => {
    const state = get();
    const snapshot = state.serverSnapshotByDate[date];
    if (!snapshot) {return;}
    set(s => {
      const nextDirty = new Set(s.dirtyDates);
      nextDirty.delete(date);
      return {
        menuStateByDate: { ...s.menuStateByDate, [date]: cloneSlot(snapshot) },
        dirtyDates: nextDirty,
      };
    });
  },

  updateItemQuantity: (menu_builder_id, itemId, isPlus) => {
    const { menuStateByDate, selectedDate } = get();
    const slot = menuStateByDate[selectedDate];
    if (!slot) {
      return { success: false, reason: 'No menu data for this date' };
    }

    const { menuBuilders, dayConfig } = slot;
    const day_config = dayConfig ?? null;
    const found = getBuilderAndItem(menuBuilders, menu_builder_id, itemId);
    if (!found) {
      return { success: false, reason: 'Item not found' };
    }

    const { builder, item } = found;
    const meals_count = dayConfig?.mealsCount ?? 0;
    const snacks_count = dayConfig?.snacksCount ?? 0;
    const is_meal = (item.food_type_category ?? '').toLowerCase() === 'meal';
    const subscription_limit =
      dayConfig?.selectionLimit && typeof dayConfig.selectionLimit === 'object' && !Array.isArray(dayConfig.selectionLimit)
        ? (dayConfig.selectionLimit as Record<number, number>)[builder.food_type_id]
        : undefined;
    const daily_cap = is_meal ? meals_count : snacks_count;
    const section_limit =
      typeof subscription_limit === 'number' && subscription_limit > 0
        ? Math.min(subscription_limit, daily_cap)
        : daily_cap;

    const current_qty = item.quantity ?? 0;
    const new_qty = isPlus ? current_qty + 1 : Math.max(0, current_qty - 1);

    if (isPlus) {
      const section_selected = builder.selected_quantity ?? 0;
      if (section_limit > 0 && section_selected >= section_limit) {
        return { success: false, reason: `Section limit reached (${section_limit})` };
      }
      const total_meals = menuBuilders.filter(b => (b.type_data?.[0]?.food_type_category ?? '').toLowerCase() === 'meal').reduce((s, b) => s + (b.selected_quantity ?? 0), 0);
      const total_snacks = menuBuilders.filter(b => (b.type_data?.[0]?.food_type_category ?? '').toLowerCase() !== 'meal').reduce((s, b) => s + (b.selected_quantity ?? 0), 0);
      if (is_meal && meals_count > 0 && total_meals >= meals_count) {return { success: false, reason: `Daily meal limit reached (${meals_count})` };}
      if (!is_meal && snacks_count > 0 && total_snacks >= snacks_count) {return { success: false, reason: `Daily snack limit reached (${snacks_count})` };}
    }

    let next_meal_selection = 0;
    if (is_meal && isPlus && current_qty === 0) {
      for (const b of slot.menuBuilders) {
        for (const it of b.type_data ?? []) {
          const q = it.quantity ?? 0;
          const meal = ((it as any).food_type_category ?? '').toLowerCase() === 'meal';
          if (meal && q > 0) {
            const sn = Number((it as any).selection_number ?? 0);
            if (sn > next_meal_selection) {next_meal_selection = sn;}
          }
          for (const r of (it as any).replaceable_menu_items ?? []) {
            const rq = r.quantity ?? 0;
            const rmeal = ((r as any).food_type_category ?? '').toLowerCase() === 'meal';
            if (rmeal && rq > 0) {
              const rsn = Number((r as any).selection_number ?? 0);
              if (rsn > next_meal_selection) {next_meal_selection = rsn;}
            }
          }
        }
      }
      next_meal_selection += 1;
    }

    /** Must match the exact builder row `getBuilderAndItem` used — `.find()` by id/food_type_id could hit the wrong builder and leave quantity at 0 while still marking dirty → empty `menu_items` POST. */
    const builder_index = indexOfBuilderForMutation(slot, found);
    if (builder_index < 0) {
      return { success: false, reason: 'Builder not found' };
    }

    const nextSlot = cloneSlot(slot);
    const b = nextSlot.menuBuilders[builder_index];
    if (b?.type_data) {
      for (const it of b.type_data) {
        if (Number(it.id) === Number(itemId)) {
          it.quantity = new_qty;
          it.is_selected = new_qty > 0;
          if (day_config?.is_variable_carbs && isPlus && current_qty === 0) {
            (it as any).scaled_carbs = resolveScaledCarbsOnFirstSelect(
              (it as any).scaled_carbs,
              day_config.default_scaled_carbs,
            );
          }
          if (is_meal) {
            (it as any).selection_number =
              isPlus && current_qty === 0 ? next_meal_selection : !isPlus ? 0 : (it as any).selection_number;
          }
          break;
        }
        const repl = (it as any).replaceable_menu_items;
        if (repl) {
          for (const r of repl) {
            if (Number(r.id) === Number(itemId)) {
              r.quantity = new_qty;
              r.is_selected = new_qty > 0;
              if (day_config?.is_variable_carbs && isPlus && current_qty === 0) {
                (r as any).scaled_carbs = resolveScaledCarbsOnFirstSelect(
                  (r as any).scaled_carbs,
                  day_config.default_scaled_carbs,
                );
              }
              if (is_meal) {
                (r as any).selection_number =
                  isPlus && current_qty === 0 ? next_meal_selection : !isPlus ? 0 : (r as any).selection_number;
              }
              break;
            }
          }
        }
      }
      if (is_meal) {
        compactMealSelectionInPlace(b.type_data);
      }
      b.selected_quantity = (b.type_data ?? []).reduce(
        (sum, it) =>
          sum +
          (it.quantity ?? 0) +
          ((it as any).replaceable_menu_items ?? []).reduce((s: number, r: any) => s + (r.quantity ?? 0), 0),
        0,
      );
    }

    set(state => {
      const d = state.selectedDate;
      const { syncVersion, dirtyDelta } = withDirtyReconcile(state, d, nextSlot);
      const nextDirty = new Set(state.dirtyDates);
      dirtyDelta(nextDirty);
      return {
        menuStateByDate: { ...state.menuStateByDate, [d]: nextSlot },
        syncVersionByDate: { ...state.syncVersionByDate, [d]: syncVersion },
        dirtyDates: nextDirty,
      };
    });
    return { success: true };
  },

  updateItemScaledCarbs: (menu_builder_id, itemId, scaledCarbs) => {
    const { menuStateByDate, selectedDate } = get();
    const slot = menuStateByDate[selectedDate];
    if (!slot) {return;}
    const found = getBuilderAndItem(slot.menuBuilders, menu_builder_id, itemId);
    if (!found) {return;}
    const carbs_builder_index = indexOfBuilderForMutation(slot, found);
    if (carbs_builder_index < 0) {return;}
    const nextSlot = cloneSlot(slot);
    const b = nextSlot.menuBuilders[carbs_builder_index];
    if (b?.type_data) {
      for (const it of b.type_data) {
        if (Number(it.id) === Number(itemId)) {
          (it as any).scaled_carbs = scaledCarbs;
          break;
        }
        const repl = (it as any).replaceable_menu_items;
        if (repl) {
          let updated = false;
          for (const r of repl) {
            if (Number(r.id) === Number(itemId)) {
              (r as any).scaled_carbs = scaledCarbs;
              updated = true;
              break;
            }
          }
          if (updated) {
            break;
          }
        }
      }
    }
    set(state => {
      const d = state.selectedDate;
      const { syncVersion, dirtyDelta } = withDirtyReconcile(state, d, nextSlot);
      const nextDirty = new Set(state.dirtyDates);
      dirtyDelta(nextDirty);
      return {
        menuStateByDate: { ...state.menuStateByDate, [d]: nextSlot },
        syncVersionByDate: { ...state.syncVersionByDate, [d]: syncVersion },
        dirtyDates: nextDirty,
      };
    });
  },

  prepareBookingData: date => {
    const d = date ?? get().selectedDate;
    const slot = get().menuStateByDate[d];
    const menuBuilders = slot?.menuBuilders ?? [];
    return buildBookingPayloadFromBuilders(menuBuilders);
  },

  resetSession: () =>
    set({
      menuStateByDate: {},
      serverSnapshotByDate: {},
      syncVersionByDate: {},
      selectedDate: '',
      dirtyDates: new Set(),
    }),
}));
